import { Stack, Typography } from '@mui/material';
import { isNil } from 'lodash-es';

import { ColumnDef } from '@/components/elements/table/types';
import GenericTableWithData, {
  Props as GenericTableWithDataProps,
} from '@/modules/dataFetching/components/GenericTableWithData';
import HmisEnum from '@/modules/hmis/components/HmisEnum';
import { HudRecordMetadataHistoryColumn } from '@/modules/hmis/components/HudRecordMetadata';
import { useProjectDashboardContext } from '@/modules/projects/components/ProjectDashboard';
import { ProjectDashboardRoutes } from '@/routes/routes';
import { HmisEnums } from '@/types/gqlEnums';
import {
  GetProjectProjectCocsDocument,
  GetProjectProjectCocsQuery,
  GetProjectProjectCocsQueryVariables,
  ProjectCocFieldsFragment,
} from '@/types/gqlTypes';
import { generateSafePath } from '@/utils/pathEncoding';

const columns: ColumnDef<ProjectCocFieldsFragment>[] = [
  {
    header: 'CoC Code',
    render: 'cocCode',
    key: 'cocCode',
  },
  {
    header: 'Geocode',
    render: 'geocode',
    key: 'geocode',
  },
  {
    header: 'Geography Type',
    key: 'geographyType',
    render: (c) => (
      <HmisEnum enumMap={HmisEnums.GeographyType} value={c.geographyType} />
    ),
  },
  {
    header: 'Address',
    key: 'address',
    render: (c: ProjectCocFieldsFragment) => (
      <Stack gap={0.5} sx={{ py: 0.5 }}>
        <Typography variant='body2'>
          {[c.address1, c.address2].filter((f) => !isNil(f)).join(', ')}
        </Typography>
        <Typography variant='body2'>
          {[c.city, c.state, c.zip].filter((f) => !isNil(f)).join(', ')}
        </Typography>
      </Stack>
    ),
  },
  HudRecordMetadataHistoryColumn,
];

type Props = Omit<
  GenericTableWithDataProps<
    GetProjectProjectCocsQuery,
    GetProjectProjectCocsQueryVariables,
    ProjectCocFieldsFragment
  >,
  'queryVariables' | 'queryDocument' | 'pagePath'
>;

const ProjectCocTable = (props: Props) => {
  const { project } = useProjectDashboardContext();

  return (
    <>
      <GenericTableWithData
        queryVariables={{ id: project.id }}
        queryDocument={GetProjectProjectCocsDocument}
        columns={columns}
        pagePath='project.projectCocs'
        noData='No Project CoC records'
        rowLinkTo={
          project.access.canEditProjectDetails
            ? (record) =>
                generateSafePath(ProjectDashboardRoutes.EDIT_COC, {
                  projectId: project.id,
                  cocId: record.id,
                })
            : undefined
        }
        rowActionTitle='Edit CoC'
        rowName={(row) => row.cocCode || row.id}
        {...props}
      />
    </>
  );
};
export default ProjectCocTable;
